import React, { useState } from 'react';
import { Button, Dropdown, Modal, Tabs, Tag, message } from 'antd';
import { useGetShootFilesForSku } from '../../hooks/useFiles';
import { useGetSkuDetailsById, useUpdateCCRequired, useUpdateCsvData } from '../../hooks/useSkus';
import stl from 'string-to-color';
import { useGetCSVFilterData } from '../../hooks/useCompanies';
import useLogout from '../../hooks/useLogout';
import OverviewTab from './skuModal/OverviewTab';
import ImagesTab from './skuModal/ImagesTab';
import CommentsTab from './skuModal/CommentsTab';
import TeamTab from './skuModal/TeamTab';
import ClientDataTab from './skuModal/ClientDataTab';
import SkuActivity from './SkuActivity';
import styles from './skuModal/modal.module.css';

function configSku(csvData) {
    if (!csvData) return '';
    const match = Object.keys(csvData).find(key => key.toLowerCase().replace(/\s/g, '') === 'configsku' || key.toLowerCase() === 'config_sku');
    return match ? csvData[match] : '';
}

function MoreDetails({ sku }) {
    const createdBy = sku?.article?.createdBy;
    const rows = [
        ['Order', sku?.article?.orderName],
        ['Article', sku?.article?.articleName || sku?.article?._id],
        ['EAN', sku?.csvData?.EAN || sku?.csvData?.barcode || sku?.skuName],
        ['Config SKU', configSku(sku?.csvData)],
        ['Barcodes', (sku?.barcodes || []).join(', ')],
        ['Inbound', createdBy ? `${createdBy.name || ''} (${createdBy.email || ''})` : ''],
        ['Raw QC', sku?.rawQcUser ? `${sku.rawQcUser.name || ''} (${sku.rawQcUser.email || ''})` : ''],
        ['Outbound', sku?.isOutbound ? sku?.addedToOutboundBoxBy : ''],
    ].filter(([, value]) => value);
    return (
        <Dropdown
            trigger={['click']}
            dropdownRender={() => (
                <div className={styles.moreDetails}>
                    {rows.map(([label, value]) => (
                        <div className={styles.moreRow} key={label}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>
            )}
        >
            <Button>More details</Button>
        </Dropdown>
    );
}

export default function SkuDetails({ onCancel, open, skuId, selectedSKU, embedded = false }) {
    const { data: files, isFetching: isFetchingFiles } = useGetShootFilesForSku(skuId);
    const { data: sku, refetch: refetchSkuDetails } = useGetSkuDetailsById(skuId);
    const [tab, setTab] = useState('overview');
    const [editValues, setEditValues] = useState(false);
    const [updatedValues, setUpdatedValues] = useState(null);
    const [csvFilterData, setCSVFilterData] = useState([]);
    const UpdateCCRequiredMutation = useUpdateCCRequired();
    const { logout } = useLogout();
    const { mutate: UpdateCsvData } = useUpdateCsvData(onUpdateSuccsess, onUpdateError);
    const [messageApi, messageApiContext] = message.useMessage();

    function onUpdateSuccsess() {
        setEditValues(false);
        setUpdatedValues(null);
        refetchSkuDetails();
    }

    function onUpdateError(err) {
        console.log(err);
        messageApi.error(err?.response?.data ? JSON.parse(err?.response?.data)?.message : err?.status);
    }

    const HandleCCRequiredUpdate = isCCRequired => {
        UpdateCCRequiredMutation.mutateAsync([skuId, isCCRequired])
            .then(() => { })
            .catch(() => { })
            .finally(() => {
                refetchSkuDetails();
            });
    };

    useGetCSVFilterData(
        data => setCSVFilterData(data?.data || []),
        error => {
            console.log(error);
            if (error?.response?.status === 401) logout();
        },
        selectedSKU?.osid,
        editValues
    );

    const openEditSection = () => {
        setEditValues(true);
        setUpdatedValues(sku?.csvData);
    };

    const handleChange = (row, val) => {
        setUpdatedValues(current => ({ ...(current || sku?.csvData), [row.key]: val }));
    };

    const handleUpdateCsvValues = () => {
        const data = {
            csvData: updatedValues,
            skuId,
            orderId: sku.oid,
        };
        const submitCsvUpdate = () => UpdateCsvData(data);
        if (sku?.isDDApproved) {
            Modal.confirm({
                content: 'This will raise a request to "Resend SKU to Production"',
                zIndex: 2000,
                onOk: submitCsvUpdate,
            });
            return;
        }
        submitCsvUpdate();
    };

    const displayName = sku?.csvData?.EAN || sku?.csvData?.barcode || sku?.skuName || '';
    const config = configSku(sku?.csvData);
    const header = (
        <div className={styles.header}>
            <div className={styles.headerMain}>
                <Tag color={stl(sku?.article?.companyCode)}>{sku?.article?.companyCode}</Tag>
                <div>
                    <p className={styles.skuName}>SKU {displayName}</p>
                    <p className={styles.skuMeta}>
                        {[config, sku?.article?.orderName].filter(Boolean).join(' · ')}
                    </p>
                </div>
            </div>
            <MoreDetails sku={sku} />
        </div>
    );
    const content = (
        <>
            {messageApiContext}
            {embedded ? header : null}
            <div className={styles.body}>
                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        {
                            key: 'overview',
                            label: 'Overview',
                            children: (
                                <OverviewTab
                                    sku={sku}
                                    files={isFetchingFiles ? [] : files}
                                    onOpenTab={setTab}
                                    onCCRequired={HandleCCRequiredUpdate}
                                />
                            ),
                        },
                        {
                            key: 'images',
                            label: 'Images & presets',
                            children: <ImagesTab sku={sku} files={isFetchingFiles ? [] : files} />,
                        },
                        {
                            key: 'activity',
                            label: 'Activity',
                            children: <SkuActivity skuId={skuId} sku={sku} />,
                        },
                        {
                            key: 'comments',
                            label: 'Comments',
                            children: <CommentsTab sku={sku} />,
                        },
                        {
                            key: 'team',
                            label: 'Team',
                            children: <TeamTab sku={sku} onOpenTab={setTab} />,
                        },
                        {
                            key: 'client',
                            label: 'Client data',
                            children: (
                                <ClientDataTab
                                    sku={sku}
                                    editing={editValues}
                                    updatedValues={updatedValues}
                                    csvFilterData={csvFilterData}
                                    onStartEdit={openEditSection}
                                    onCancelEdit={() => setEditValues(false)}
                                    onChange={handleChange}
                                    onSave={handleUpdateCsvValues}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );

    if (embedded) return <div className={`${styles.embedded} ${styles.skuModal}`}>{content}</div>;

    return (
        <Modal
            className={styles.skuModal}
            width="92vw"
            onCancel={onCancel}
            open={open}
            footer={<Button onClick={onCancel}>Close</Button>}
            title={header}
            style={{ top: 16 }}
            bodyStyle={{ background: '#f5f6f8' }}
        >
            {content}
        </Modal>
    );
}
